import { useEffect } from "react";
import moment from "moment";
import { useSetRecoilState } from "recoil";
import { jwtDecode } from "jwt-decode";
import Ajax from "./utils/api";
import ajaxRoutes from "./utils/ajaxRoutes";
import { initializingAtom, userAtom, categoriesAtom } from "./globalAtoms";

function useInit() {
	const setUser = useSetRecoilState(userAtom);
	const setCategories = useSetRecoilState(categoriesAtom);
	const setInit = useSetRecoilState(initializingAtom);

	useEffect(() => {
		async function init() {
			const token = localStorage.getItem("creator_jwt_token");

			// If there's no token, finish initialization
			if (!token) {
				setInit(false);
				return;
			}

			let decoded;
			try {
				decoded = jwtDecode(token);
			} catch (error) {
				localStorage.removeItem("creator_jwt_token");
				setUser(null);
				setInit(false);
				return;
			}

			// Check token expiration
			const currentTime = Date.now() / 1000;
			if (decoded.exp < currentTime) {
				localStorage.removeItem("creator_jwt_token");
				setUser(null);
				setInit(false);
				return;
			}

			try {
				Ajax.headers["Authorization"] = `Bearer ${token}`;
				await getUser(setUser, setCategories);
			} catch (error) {
				console.error("Initialization error:", error);
			} finally {
				setInit(false);
			}
		}

		init();
	}, [setInit, setUser, setCategories]);
}

export const getMembers = async (gymId, setUser) => {
	try {
		const response = await Ajax.call(ajaxRoutes.GET_GYM_MEMBER_LIST(gymId));
		if (response?.users) {
			// Calculate the number of members per month/year
			const overTimeData = response.users.reduce((acc, user) => {
				const formatted = moment(user.created_date).format("MMMM-YYYY");
				acc[formatted] = (acc[formatted] || 0) + 1;
				return acc;
			}, {});

			setUser((prev) => ({
				...prev,
				members: response.users,
				overTimeData,
			}));
		}
	} catch (error) {
		console.error("Error fetching gym members:", error);
	}
};

export const getUser = async (setUser, setCategories) => {
	try {
		const user = await Ajax.call(ajaxRoutes.GET_USER(), "get", null, (error) => {
			if (error?.status === 503 || error?.response?.status === 503) {
				localStorage.removeItem("creator_jwt_token");
				window.location.href = "/error/503";
			}
		});
		const userAssociations = await Ajax.call(ajaxRoutes.GET_USER_ASSOCIATIONS(), "get");
		const token = localStorage.getItem("creator_jwt_token");
		const decoded = token ? jwtDecode(token) : null;
		const tokenGymId = decoded?.gym_id;

		// If there are no associations, return the basic user info
		if (!userAssociations || Object.keys(userAssociations).length === 0) {
			setUser(user);
			return;
		}

		// If we don't have a gym id from the token, just update user with associations
		if (!tokenGymId) {
			setUser({ ...user, userAssociations });
			return;
		}

		const gym = await Ajax.call(ajaxRoutes.GET_GYM(tokenGymId), "get");
		if (!gym || !gym.id) {
			setUser({ ...user, userAssociations });
			return;
		}

		// Get additional data concurrently
		const [gymBranding, pages, workoutsResponse] = await Promise.all([
			Ajax.call(ajaxRoutes.GET_GYM_BRANDING(gym.id), "get"),
			Ajax.call(ajaxRoutes.GET_PAGES(gym.id), "get"),
			Ajax.call(ajaxRoutes.GET_WORKOUTS_BY_GYM_ID(gym.id), "get"),
		]);

		// Optionally load gym categories
		if (setCategories) {
			const categoriesResponse = await Ajax.call(ajaxRoutes.GET_CATEGORIES(gym.id), "get");
			if (categoriesResponse?.categories) {
				setCategories(categoriesResponse.categories);
			}
		}

		// Set the user state with additional info
		setUser({
			...user,
			userAssociations,
			gym,
			domainGym: gym,
			gymBranding,
			pages,
			workouts: workoutsResponse?.workouts,
			domainWorkouts: workoutsResponse?.workouts,
		});
	} catch (error) {
		console.error("Error fetching user data:", error);
	}
};

export default useInit;
